import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

/**
 * shadcn/ui Badge, in the AeroVect palette.
 *
 * The tones match the severity vocabulary the schema already speaks (ok / warn
 * / bad / neutral) so a badge and a value chip on the same row can never
 * disagree about what "warn" looks like. `count` is the one addition: a filled
 * pill for "3 pending", which reads as a quantity rather than a status.
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1 whitespace-nowrap rounded border font-sans font-bold uppercase tracking-[0.04em] text-[11px] leading-normal px-1.5 py-px",
  {
    variants: {
      tone: {
        neutral: "border-line bg-offWhite text-slate",
        ok: "border-readyText/35 bg-readyText/10 text-readyText",
        warn: "border-majorText/35 bg-majorText/10 text-majorText",
        bad: "border-groundedDot/35 bg-groundedDot/10 text-groundedText",
        count:
          "border-blue bg-blue text-white rounded-full px-2 tracking-normal normal-case font-semibold",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, tone, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(badgeVariants({ tone }), className)}
      {...props}
    />
  ),
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };
